/**
 * Cushion Recent Days Table Component - Display last 3 days of cushion production data
 */

import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Package, Clock } from 'lucide-react';
import { DailyCushionData } from '@/types/cushion';
import { formatNumberDisplay } from '@/lib/utils';

interface CushionRecentDaysTableProps {
  dailyData: DailyCushionData[];
  isLoading?: boolean;
  error?: string | null;
  processes?: string[];
  title?: string;
}

export const CushionRecentDaysTable: React.FC<CushionRecentDaysTableProps> = ({ 
  dailyData, 
  isLoading = false, 
  error = null,
  processes = ['catVai', 'may', 'thanhPham', 'doMut', 'catMut', 'gon'],
  title = "Ba ngày gần nhất"
}) => {
  // Process configurations
  const processConfig = {
    catVai: { name: 'Cắt vải', shortName: 'Cắt vải', color: 'bg-blue-100' },
    may: { name: 'May', shortName: 'May', color: 'bg-green-100' },
    thanhPham: { name: 'Thành phẩm', shortName: 'T.Phẩm', color: 'bg-amber-100' },
    doMut: { name: 'Đổ mút', shortName: 'Đổ mút', color: 'bg-teal-100' },
    catMut: { name: 'Cắt mút', shortName: 'Cắt mút', color: 'bg-red-100' },
    gon: { name: 'Gòn', shortName: 'Gòn', color: 'bg-gray-100' }
  };

  // Get last 3 days sorted by date (newest first)
  const recentData = useMemo(() => {
    return [...dailyData]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .slice(0, 3);
  }, [dailyData]);

  // Calculate min/max for M³ values in each process for conditional highlighting
  const processM3Stats = useMemo(() => {
    if (recentData.length === 0) return {};
    // Filter out 'gon' since it doesn't have volume
    const volumeProcesses = processes.filter(p => p !== 'gon');
    const stats: Record<string, { min: number; max: number }> = {};
    
    volumeProcesses.forEach(process => {
      const values = recentData
        .map(item => item.processes[process]?.volume)
        .filter((value): value is number => value !== null && value !== undefined && value > 0);
      
      if (values.length > 1) { // Only highlight if we have multiple values to compare
        stats[process] = {
          min: Math.min(...values),
          max: Math.max(...values)
        };
      }
    });
    
    return stats;
  }, [recentData, processes]);

  // Get conditional styling for M³ values
  const getM3Styling = (value: number | null, processName: string) => {
    if (!value || value <= 0 || !processM3Stats[processName]) {
      return 'text-sm text-center bg-amber-50 font-semibold border-r';
    }
    
    const { min, max } = processM3Stats[processName];
    if (value === max) {
      return 'text-sm text-center bg-green-600 text-white font-semibold border-r'; // Highest value: green background, white text
    } else if (value === min) {
      return 'text-sm text-center bg-red-600 text-white font-semibold border-r'; // Lowest value: red background, white text
    }
    
    return 'text-sm text-center bg-amber-50 font-semibold border-r'; // Default amber styling
  };

  // Format number with decimal places
  // Use Vietnamese locale formatting for numbers

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Ba ngày gần nhất
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Ba ngày gần nhất
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4 text-red-600">
            <p>Lỗi tải dữ liệu: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-green-600" />
          📦 {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {recentData.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                {/* First row: Process names (merged cells) */}
                <TableRow>
                  <TableHead rowSpan={2} className="text-center font-medium w-[120px] border-r">Ngày</TableHead>
                  
                  {/* Dynamic process headers with merged cells */}
                  {processes.map((process, index) => {
                    const config = processConfig[process as keyof typeof processConfig];
                    const isLast = index === processes.length - 1;
                    return (
                      <TableHead 
                        key={process}
                        colSpan={3} 
                        className={`text-center font-medium ${config.color} ${isLast ? '' : 'border-r'}`}
                      >
                        {config.name}
                      </TableHead>
                    );
                  })}
                </TableRow>
                
                {/* Second row: Column labels */}
                <TableRow>
                  {/* Dynamic column labels for each process */}
                  {processes.map((process, index) => {
                    const isLast = index === processes.length - 1;
                    return (
                      <React.Fragment key={`cols-${process}`}>
                        <TableHead className="text-xs text-center border-r">Công</TableHead>
                        <TableHead className="text-xs text-center border-r">
                          {process === 'gon' ? 'Kg' : 'M³'}
                        </TableHead>
                        <TableHead className={`text-xs text-center ${isLast ? '' : 'border-r'}`}>NS</TableHead>
                      </React.Fragment>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentData.map((item, index) => (
                  <TableRow key={index} className="hover:bg-gray-50">
                    <TableCell className="font-medium text-center border-r">
                      {new Date(item.date).toLocaleDateString('vi-VN')}
                    </TableCell>
                    
                    {/* Dynamic data cells based on selected processes */}
                    {processes.map((process, index) => {
                      const processData = item.processes[process as keyof typeof item.processes];
                      const isLast = index === processes.length - 1;
                      
                      return (
                        <React.Fragment key={`data-${process}`}>
                          {/* Labor */}
                          <TableCell className="text-sm text-center border-r">
                            {formatNumberDisplay(processData.labor || 0, 0)}
                          </TableCell>
                          
                          {/* Volume/Weight */}
                          <TableCell className={
                            process === 'gon' 
                              ? "text-sm text-center bg-gray-50/30 border-r" 
                              : getM3Styling(processData.volume, process)
                          }>
                                                      {process === 'gon' 
                            ? formatNumberDisplay(processData.weight || 0, 2)
                            : formatNumberDisplay(processData.volume || 0, 2)
                          }
                          </TableCell>
                          
                          {/* Productivity */}
                          <TableCell className={`text-sm text-center ${isLast ? '' : 'border-r'}`}>
                            {formatNumberDisplay(processData.productivity || 0, 3)}
                          </TableCell>
                        </React.Fragment>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">Không có dữ liệu sản xuất nệm mút</p>
            <p className="text-sm">Hãy thử điều chỉnh bộ lọc hoặc kiểm tra kết nối dữ liệu</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};